import { useEffect, useState, useCallback, useMemo, Fragment, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, IconButton, Tooltip, Alert, TextField, InputAdornment, Switch,
  TablePagination, Snackbar, Select, MenuItem, Menu, ListItemIcon, ListItemText,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Divider,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ShareIcon from '@mui/icons-material/Share';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PublicIcon from '@mui/icons-material/Public';
import LockIcon from '@mui/icons-material/Lock';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { fetchScans, removeScan } from '../store/scanSlice.js';
import type { RootState, AppDispatch } from '../store/index.js';
import { colors } from '../styles/theme.js';
import { Rating, SCAN_LABEL_KEYS, type IScan } from '../../shared/types.js';
import LoadingSpinner from '../components/LoadingSpinner.js';
import ScanStatusChip from '../components/ScanStatusChip.js';
import { useScanStatusPolling } from '../hooks/useScanStatusPolling.js';
import { api } from '../api/client.js';
import { useAuth } from '../hooks/useAuth.js';
import { buildShareUrl } from '../shareUrl.js';
import {
  SCORE_CARD_HTML_TEMPLATE_IDS,
  SCORE_CARD_HTML_TEMPLATE_LABELS,
  normalizeScoreCardHtmlTemplate,
  type ScoreCardHtmlTemplateId,
} from '../../shared/scoreCardHtmlTemplates.js';

const sparkleColorByDomain: Record<string, string> = {
  'ambition-performance.de': colors.lime,
  'mso-digital.de': '#2596be',
  'symplr.de': '#f7952e',
};

function formatArchiveUrl(rawUrl: string): string {
  const trimmed = rawUrl.trim();
  if (!trimmed) return '';

  const withoutProtocol = trimmed.replace(/^https?:\/\//i, '');
  const withoutWww = withoutProtocol.replace(/^www\./i, '');
  return withoutWww.replace(/\/+$/, '');
}

function sparkleColorForUrl(rawUrl: string): string | null {
  const formatted = formatArchiveUrl(rawUrl).toLowerCase();
  const domain = formatted.split('/')[0];
  return sparkleColorByDomain[domain] ?? null;
}

type ColKey = 'scannedBy' | 'template' | 'htmlTemplate';

const COL_VISIBLE_KEY = 'archive_col_visible_v1';
const defaultColVisible: Record<ColKey, boolean> = { scannedBy: true, template: true, htmlTemplate: true };

function loadColVisible(): Record<ColKey, boolean> {
  try {
    const raw = localStorage.getItem(COL_VISIBLE_KEY);
    if (!raw) return defaultColVisible;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (
      typeof parsed.scannedBy === 'boolean' &&
      typeof parsed.template === 'boolean' &&
      typeof parsed.htmlTemplate === 'boolean'
    ) {
      return { scannedBy: parsed.scannedBy, template: parsed.template, htmlTemplate: parsed.htmlTemplate };
    }
  } catch { /* ignore */ }
  return defaultColVisible;
}

export default function ArchivePage() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { scans, loading, error } = useSelector((state: RootState) => state.scan);

  // Smart Polling: ruft periodisch /scans/active-statuses ab und patcht die
  // Archiv-Liste inline, bis keine aktiven Scans mehr existieren.
  useScanStatusPolling(true);

  const OVERFETCH = 4;
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [shareSnackbar, setShareSnackbar] = useState(false);
  const [selectedTemplates, setSelectedTemplates] = useState<Record<number, ScoreCardHtmlTemplateId>>({});
  const [colVisible, setColVisible] = useState<Record<ColKey, boolean>>(loadColVisible);
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
  const [clientPage, setClientPage] = useState(0);
  const [clientRowsPerPage, setClientRowsPerPage] = useState(25);
  const [actionMenu, setActionMenu] = useState<{ el: HTMLElement; scan: IScan } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<IScan | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [restartConfirm, setRestartConfirm] = useState<IScan | null>(null);
  const [restartLoading, setRestartLoading] = useState(false);
  const [restartSnackbar, setRestartSnackbar] = useState(false);
  const [showDevScans, setShowDevScans] = useState(false);

  // Auto-enable dev scan toggle when user has dev_mode active
  useEffect(() => {
    setShowDevScans(!!user?.dev_mode);
  }, [user?.dev_mode]);

  const handleActionMenuOpen = useCallback((e: React.MouseEvent<HTMLElement>, scan: IScan) => {
    e.stopPropagation();
    setActionMenu({ el: e.currentTarget, scan });
  }, []);

  const handleActionMenuClose = useCallback(() => setActionMenu(null), []);

  const handleDeleteClick = useCallback(() => {
    if (!actionMenu) return;
    setDeleteConfirm(actionMenu.scan);
    setActionMenu(null);
  }, [actionMenu]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteConfirm) return;
    setDeleteLoading(true);
    try {
      const res = await api.delete(`/scans/${deleteConfirm.id}`);
      if (res.success) {
        dispatch(removeScan(deleteConfirm.id));
      }
    } finally {
      setDeleteLoading(false);
      setDeleteConfirm(null);
    }
  }, [deleteConfirm, dispatch]);

  const handleRestartClick = useCallback(() => {
    if (!actionMenu) return;
    setRestartConfirm(actionMenu.scan);
    setActionMenu(null);
  }, [actionMenu]);

  useEffect(() => {
    localStorage.setItem(COL_VISIBLE_KEY, JSON.stringify(colVisible));
  }, [colVisible]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const serverLimit = clientRowsPerPage * OVERFETCH;

  const loadScans = useCallback(() => {
    dispatch(fetchScans({ page: 1, limit: serverLimit, search: debounced || undefined }));
  }, [dispatch, serverLimit, debounced]);

  const handleRestartConfirm = useCallback(async () => {
    if (!restartConfirm) return;
    setRestartLoading(true);
    try {
      const res = await api.post(`/scans/${restartConfirm.id}/restart`);
      if (res.success) {
        setRestartSnackbar(true);
        loadScans();
      }
    } finally {
      setRestartLoading(false);
      setRestartConfirm(null);
    }
  }, [restartConfirm, loadScans]);

  useEffect(() => {
    dispatch(fetchScans({ page: 1, limit: serverLimit, search: debounced || undefined }));
    setClientPage(0);
  }, [dispatch, debounced, serverLimit]);

  const handlePageChange = (_: unknown, newPage: number) => {
    setClientPage(newPage);
  };

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setClientRowsPerPage(parseInt(e.target.value, 10));
    setClientPage(0);
  };

  const getShareUrl = useCallback((token: string) => buildShareUrl(token), []);

  useEffect(() => {
    setSelectedTemplates((prev) => {
      const next = { ...prev };
      for (const scan of scans) {
        if (!next[scan.id]) {
          next[scan.id] = normalizeScoreCardHtmlTemplate(scan.scan_template?.score_card_html_template);
        }
      }
      return next;
    });
  }, [scans]);

  const getSelectedTemplate = useCallback((scanId: number): ScoreCardHtmlTemplateId => {
    return selectedTemplates[scanId] ?? normalizeScoreCardHtmlTemplate(null);
  }, [selectedTemplates]);

  const buildTemplateShareUrl = useCallback((token: string, templateId: ScoreCardHtmlTemplateId) => {
    const url = new URL(getShareUrl(token));
    url.searchParams.set('template', templateId);
    return url.toString();
  }, [getShareUrl]);

  const handleShare = useCallback((token: string, templateId: ScoreCardHtmlTemplateId) => {
    const url = buildTemplateShareUrl(token, templateId);
    navigator.clipboard.writeText(url);
    setShareSnackbar(true);
  }, [buildTemplateShareUrl]);

  const handleOpenShareReport = useCallback((token: string, templateId: ScoreCardHtmlTemplateId) => {
    const url = buildTemplateShareUrl(token, templateId);
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [buildTemplateShareUrl]);

  const handleTemplateChange = useCallback((scanId: number, templateId: ScoreCardHtmlTemplateId) => {
    setSelectedTemplates((prev) => ({ ...prev, [scanId]: templateId }));
  }, []);

  const handleTogglePublic = async (scanId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    await api.patch(`/scans/${scanId}/public`);
    loadScans();
  };

  const handleGroupTogglePublic = useCallback(async (groupScans: IScan[], e: React.MouseEvent) => {
    e.stopPropagation();
    const allPublic = groupScans.every((s) => s.is_public);
    const scansToToggle = groupScans.filter((s) => s.is_public === allPublic);
    await Promise.all(scansToToggle.map((s) => api.patch(`/scans/${s.id}/public`)));
    loadScans();
  }, [loadScans]);

  const groupedScans = useMemo(() => {
    // Filter order matters:
    // 1. Public scans: always visible to everyone (including public dev scans).
    // 2. Private dev scans (visible_in_archive=false): gated by toggle for ALL — own and others'.
    // 3. Own non-dev private scans: always visible.
    // 4. Everything else (private non-dev from others): hidden.
    const visibleScans = scans.filter((s) => {
      if (s.is_public) return true;
      if (s.score_card?.visible_in_archive === false) return !!(user?.dev_mode && showDevScans);
      if (s.user_id === user?.id) return true;
      return false;
    });

    if (debounced) {
      const q = debounced.toLowerCase().trim();
      // Flat mode while searching — filtered by URL only
      return visibleScans
        .filter((scan) => formatArchiveUrl(scan.url).toLowerCase().includes(q))
        .map((scan) => ({ domain: formatArchiveUrl(scan.url).split('/')[0], scans: [scan] }));
    }

    // Group consecutive scans of the same domain into blocks (chronological run-length encoding).
    // Scans are already sorted newest-first from the backend.
    // If the same domain is scanned again after a different domain interrupts, it forms a new group.
    const groups: { domain: string; scans: IScan[] }[] = [];
    for (const scan of visibleScans) {
      const domain = formatArchiveUrl(scan.url).split('/')[0];
      const last = groups[groups.length - 1];
      if (last && last.domain === domain) {
        last.scans.push(scan);
      } else {
        groups.push({ domain, scans: [scan] });
      }
    }
    return groups;
  }, [scans, debounced, user?.dev_mode, showDevScans]);

  const visibleGroups = useMemo(
    () => groupedScans.slice(clientPage * clientRowsPerPage, (clientPage + 1) * clientRowsPerPage),
    [groupedScans, clientPage, clientRowsPerPage],
  );

  const toggleGroup = useCallback((groupKey: number) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  }, []);

  const allColsOn = (['scannedBy', 'template', 'htmlTemplate'] as ColKey[]).every((k) => colVisible[k]);

  const handleAllToggle = useCallback(() => {
    const newVal = !allColsOn;
    setColVisible({ scannedBy: newVal, template: newVal, htmlTemplate: newVal });
  }, [allColsOn]);

  const handleColToggle = useCallback((key: ColKey) => {
    setColVisible((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const totalColCount = useMemo(() => {
    let count = 6; // URL, Status, Score, Datum, Public, Actions
    if (colVisible.scannedBy) count++;
    if (colVisible.template) count++;
    if (colVisible.htmlTemplate) count++;
    return count;
  }, [colVisible]);

  const scanRows = useMemo(() => {
    const renderRow = (scan: IScan, isChild: boolean) => {
      const isCompleted = scan.scan_label?.key_name === SCAN_LABEL_KEYS.COMPLETED;
      const scores = isCompleted
        ? (scan.score_card?.scores_json as { overall_score: number; overall_rating: Rating } | null)
        : null;
      const shareToken = scan.score_card?.share_token;
      const isOwn = scan.user_id === user?.id;
      const sparkleColor = sparkleColorForUrl(scan.url);
      const hasWaveTextEffect = Boolean(sparkleColor);
      const waveDuration = '3.6s';

      const scanHref = `/scan/${scan.id}`;
      // Middle-click opens in a new tab via mouseDown (suppress autoscroll) + mouseUp (trigger);
      // Ctrl/Cmd+left-click opens in a new tab; plain left-click uses SPA navigation.
      const handleRowMouseDown = (e: React.MouseEvent<HTMLTableRowElement>) => {
        if (e.button === 1) {
          e.preventDefault(); // kill autoscroll mode before the browser activates it
        }
      };
      const handleRowMouseUp = (e: React.MouseEvent<HTMLTableRowElement>) => {
        if (e.button === 1) {
          e.preventDefault();
          window.open(scanHref, '_blank', 'noopener,noreferrer');
        }
      };
      const handleRowClick = (e: React.MouseEvent<HTMLTableRowElement>) => {
        if (e.ctrlKey || e.metaKey) {
          window.open(scanHref, '_blank', 'noopener,noreferrer');
          return;
        }
        navigate(scanHref);
      };

      return (
        <Fragment key={scan.id}>
          <TableRow hover sx={{ cursor: 'pointer', ...(isChild && { bgcolor: '#f5f7fa' }) }} onClick={handleRowClick} onMouseDown={handleRowMouseDown} onMouseUp={handleRowMouseUp}>
            <TableCell sx={isChild ? { pl: 4 } : undefined}>
              <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', maxWidth: 280 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <Box
                    component="span"
                    sx={hasWaveTextEffect ? {
                      backgroundImage: `linear-gradient(115deg, ${colors.black} 0%, ${colors.black} 35%, ${sparkleColor} 50%, ${colors.black} 65%, ${colors.black} 100%)`,
                      backgroundSize: '220% 100%',
                      color: 'transparent',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      animation: `archiveDomainWave ${waveDuration} linear infinite`,
                      '@keyframes archiveDomainWave': {
                        '0%': { backgroundPosition: '200% 0' },
                        '100%': { backgroundPosition: '-20% 0' },
                      },
                    } : undefined}
                  >
                    {formatArchiveUrl(scan.url)}
                  </Box>
                </Typography>
              </Box>
            </TableCell>
            <TableCell>
              <ScanStatusChip scanLabel={scan.scan_label} />
            </TableCell>
            <TableCell>
              {scores ? (
                <Typography variant="body2" sx={{
                  fontWeight: 600,
                  color: scores.overall_score >= 80 ? colors.scoreGreen : scores.overall_score >= 50 ? colors.scoreYellow : colors.scoreRed,
                }}>
                  {Math.round(scores.overall_score)}
                </Typography>
              ) : '—'}
            </TableCell>
            {colVisible.scannedBy && (
              <TableCell>
                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                  {scan.user?.name || scan.user?.email || '—'}
                </Typography>
              </TableCell>
            )}
            <TableCell>
              <Typography variant="body2">
                {new Date(scan.created_at).toLocaleString('de-DE', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </Typography>
            </TableCell>
            {colVisible.template && (
              <TableCell>
                <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                  {scan.scan_template?.display_name ?? '—'}
                </Typography>
              </TableCell>
            )}
            <TableCell
              align="center"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => { if (e.button === 1) e.stopPropagation(); }}
              onMouseUp={(e) => { if (e.button === 1) e.stopPropagation(); }}
            >
              {isOwn ? (
                <Tooltip title={scan.is_public ? 'Auf privat setzen' : 'Öffentlich machen'}>
                  <Switch
                    size="small"
                    checked={scan.is_public}
                    onClick={(e) => handleTogglePublic(scan.id, e)}
                  />
                </Tooltip>
              ) : (
                scan.is_public
                  ? <Tooltip title="Öffentlich"><PublicIcon fontSize="small" sx={{ color: colors.scoreGreen }} /></Tooltip>
                  : <Tooltip title="Privat"><LockIcon fontSize="small" sx={{ color: colors.black60 }} /></Tooltip>
              )}
            </TableCell>
            {colVisible.htmlTemplate && (
              <TableCell
                align="center"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => { if (e.button === 1) e.stopPropagation(); }}
                onMouseUp={(e) => { if (e.button === 1) e.stopPropagation(); }}
              >
                <Select
                  size="small"
                  value={getSelectedTemplate(scan.id)}
                  onChange={(e) => handleTemplateChange(scan.id, e.target.value as ScoreCardHtmlTemplateId)}
                  sx={{ minWidth: 150 }}
                >
                  {SCORE_CARD_HTML_TEMPLATE_IDS.map((templateId) => (
                    <MenuItem key={templateId} value={templateId}>
                      {SCORE_CARD_HTML_TEMPLATE_LABELS[templateId]}
                    </MenuItem>
                  ))}
                </Select>
              </TableCell>
            )}
            <TableCell
              align="center"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => { if (e.button === 1) e.stopPropagation(); }}
              onMouseUp={(e) => { if (e.button === 1) e.stopPropagation(); }}
            >
              <IconButton size="small" onClick={(e) => handleActionMenuOpen(e, scan)}>
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </TableCell>
          </TableRow>
        </Fragment>
      );
    };

    const rows: ReactElement[] = [];
    for (let gi = 0; gi < visibleGroups.length; gi++) {
      const group = visibleGroups[gi];
      const groupKey = group.scans[0].id;
      const isExpanded = expandedGroups.has(groupKey);
      const isLastGroup = gi === visibleGroups.length - 1;
      if (group.scans.length > 1) {
        const leftColSpan = 4 + (colVisible.scannedBy ? 1 : 0) + (colVisible.template ? 1 : 0);
        rows.push(
          <TableRow
            key={`group-${groupKey}`}
            sx={{
              cursor: 'pointer',
              bgcolor: '#eef0f5',
              '&:hover': { bgcolor: '#e2e5ed' },
              ...(isExpanded && { borderBottom: '2px solid #4b5563' }),
              ...(!isExpanded && !isLastGroup && { borderBottom: '1px solid #4b5563' }),
            }}
            onClick={() => toggleGroup(groupKey)}
          >
            <TableCell colSpan={leftColSpan} sx={{ py: 1.25, pl: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{group.domain}</Typography>
                <IconButton size="small" sx={{ p: 0.25 }} onClick={(e) => { e.stopPropagation(); toggleGroup(groupKey); }}>
                  {isExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                </IconButton>
                <Chip
                  label={`${group.scans.length}\u00d7`}
                  size="small"
                  sx={{ height: 18, fontSize: '0.7rem', fontWeight: 600, bgcolor: '#bbf7d0', color: '#166534' }}
                />
              </Box>
            </TableCell>
            <TableCell align="center" onClick={(e) => e.stopPropagation()}>
              {group.scans.some((s) => s.user_id === user?.id) ? (
                <Tooltip title={group.scans.every((s) => s.is_public) ? 'Alle auf privat' : 'Alle \u00f6ffentlich'}>
                  <Switch
                    size="small"
                    checked={group.scans.every((s) => s.is_public)}
                    onClick={(e) => { void handleGroupTogglePublic(group.scans, e); }}
                  />
                </Tooltip>
              ) : null}
            </TableCell>
            {colVisible.htmlTemplate && <TableCell />}
            <TableCell />
          </TableRow>,
        );
        if (isExpanded) {
          for (let si = 0; si < group.scans.length; si++) {
            const isLastChild = si === group.scans.length - 1;
            rows.push(
              <Fragment key={`child-${group.scans[si].id}`}>
                {renderRow(group.scans[si], true)}
                {isLastChild && (
                  <TableRow key={`sep-${groupKey}`}>
                    <TableCell colSpan={totalColCount} sx={{ p: 0, borderBottom: '2px solid #4b5563' }} />
                  </TableRow>
                )}
              </Fragment>,
            );
          }
        }
      } else {
        rows.push(renderRow(group.scans[0], false));
      }
    }
    return rows;
  }, [visibleGroups, expandedGroups, toggleGroup, colVisible, totalColCount, user?.id, navigate, handleShare, handleOpenShareReport, getSelectedTemplate, handleTemplateChange, handleTogglePublic, handleGroupTogglePublic, handleActionMenuOpen]);

  if (loading && scans.length === 0) return <LoadingSpinner message="Archiv wird geladen..." />;

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>Archiv</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Suche nach URL oder User..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            maxWidth: 420,
            width: '100%',
            '& .MuiOutlinedInput-root': {
              borderRadius: '14px',
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Column visibility filter bar + dev mode row — unified box */}
      <Box sx={{ mb: 2, bgcolor: '#f9fafb', borderRadius: 2, border: `1px solid ${colors.silver}`, overflow: 'hidden' }}>
        {/* Row 1: Spalten */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, px: 1.5, py: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', mr: 0.5 }}>Spalten:</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pr: 1.5, mr: 0.5, borderRight: `1px solid ${colors.silver}` }}>
            <Switch size="small" checked={allColsOn} onChange={handleAllToggle} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Alle</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Switch size="small" checked={colVisible.scannedBy} onChange={() => handleColToggle('scannedBy')} />
            <Typography variant="body2">Gescannt von</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Switch size="small" checked={colVisible.template} onChange={() => handleColToggle('template')} />
            <Typography variant="body2">Template</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Switch size="small" checked={colVisible.htmlTemplate} onChange={() => handleColToggle('htmlTemplate')} />
            <Typography variant="body2">HTML-Template</Typography>
          </Box>
        </Box>

        {/* Row 2: Dev mode filter — only visible when user has dev_mode active */}
        {user?.dev_mode && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1, borderTop: `1px solid ${colors.silver}` }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', mr: 0.5 }}>Entwicklermodus:</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Switch
                size="small"
                checked={showDevScans}
                onChange={() => setShowDevScans((v) => !v)}
              />
              <Typography variant="body2">Dev-Scans anzeigen</Typography>
            </Box>
          </Box>
        )}
      </Box>

      <Paper elevation={0} sx={{ border: `1px solid ${colors.silver}`, overflow: 'hidden' }}>
        <Table size="small" sx={{ width: '100%', tableLayout: 'auto' }}>
          <TableHead>
            <TableRow sx={{ bgcolor: colors.lightGrey }}>
              <TableCell>URL</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Score</TableCell>
              {colVisible.scannedBy && <TableCell>Gescannt von</TableCell>}
              <TableCell>Datum</TableCell>
              {colVisible.template && <TableCell>Template</TableCell>}
              <TableCell align="center">Public</TableCell>
              {colVisible.htmlTemplate && <TableCell align="center">HTML-Template</TableCell>}
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {scanRows}
            {scans.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={totalColCount} sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">
                    {debounced ? 'Keine Treffer für die Suche.' : 'Noch keine Scans vorhanden'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={groupedScans.length}
          page={clientPage}
          onPageChange={handlePageChange}
          rowsPerPage={clientRowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage={debounced ? 'Einträge pro Seite:' : 'Gruppen pro Seite:'}
          labelDisplayedRows={({ from, to, count: c }) => debounced ? `${from}\u2013${to} von ${c}` : `${from}\u2013${to} von ${c} Gruppen`}
        />
      </Paper>

      <Snackbar open={shareSnackbar} autoHideDuration={3000} onClose={() => setShareSnackbar(false)} message="Share-Link kopiert!" />

      {/* Action context menu */}
      <Menu
        anchorEl={actionMenu?.el}
        open={Boolean(actionMenu)}
        onClose={handleActionMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => { if (actionMenu) { navigate(`/scan/${actionMenu.scan.id}`); } handleActionMenuClose(); }}>
          <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Anzeigen</ListItemText>
        </MenuItem>
        {actionMenu && actionMenu.scan.scan_label?.key_name !== SCAN_LABEL_KEYS.COMPLETED && (
          <MenuItem onClick={handleRestartClick}>
            <ListItemIcon><RestartAltIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Scan neu starten</ListItemText>
          </MenuItem>
        )}
        {actionMenu?.scan.score_card?.share_token && [
          <MenuItem key="share" onClick={() => { if (actionMenu?.scan.score_card?.share_token) handleShare(actionMenu.scan.score_card.share_token, getSelectedTemplate(actionMenu.scan.id)); handleActionMenuClose(); }}>
            <ListItemIcon><ShareIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Share-Link kopieren</ListItemText>
          </MenuItem>,
          <MenuItem key="open" onClick={() => { if (actionMenu?.scan.score_card?.share_token) handleOpenShareReport(actionMenu.scan.score_card.share_token, getSelectedTemplate(actionMenu.scan.id)); handleActionMenuClose(); }}>
            <ListItemIcon><OpenInNewIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Share Report öffnen</ListItemText>
          </MenuItem>,
        ]}
        <Divider />
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
          <ListItemText>Scan löschen</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete confirmation dialog */}
      <Dialog open={Boolean(deleteConfirm)} onClose={() => !deleteLoading && setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Scan löschen?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Dieser Scan wird <strong>permanent gelöscht</strong> und kann nicht wiederhergestellt werden.
            <br /><br />
            <strong>{deleteConfirm ? formatArchiveUrl(deleteConfirm.url) : ''}</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)} disabled={deleteLoading}>Abbrechen</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleteLoading}>
            {deleteLoading ? 'Löschen…' : 'Endgültig löschen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restart confirmation dialog */}
      <Dialog open={Boolean(restartConfirm)} onClose={() => !restartLoading && setRestartConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Scan neu starten?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Der komplette Scan wird <strong>neu ausgeführt</strong> — Playwright-Screening, Datenerfassung
            und Auswertung laufen erneut. Vorhandene Zwischenergebnisse dieses Scans werden dabei überschrieben.
            <br /><br />
            <strong>{restartConfirm ? formatArchiveUrl(restartConfirm.url) : ''}</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestartConfirm(null)} disabled={restartLoading}>Abbrechen</Button>
          <Button onClick={handleRestartConfirm} variant="contained" disabled={restartLoading}>
            {restartLoading ? 'Wird gestartet…' : 'Neu starten'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={restartSnackbar} autoHideDuration={4000} onClose={() => setRestartSnackbar(false)} message="Scan wurde neu gestartet." />
    </Box>
  );
}
